import { lazy, Suspense, useMemo, useRef } from 'react';

import type { OnChange, OnMount, OnValidate } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';

import { cn } from '@/shadcn/lib/utils';
import { Skeleton } from '@/shadcn/skeleton';
import { useTheme } from '@/shadcn/theme/theme-provider';

import { notify } from '@/components/toast';

const MonacoEditor = lazy(async () => {
  const module = await import('@monaco-editor/react');
  return { default: module.Editor };
});

/**
 * Converts a value to a formatted JSON string.
 * If the value is already a string, attempts to parse and re-format it.
 */
const toJsonString = (value: unknown): string => {
  if (typeof value === 'string') {
    try {
      // Try to parse and re-format if it's a JSON string
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      // If parsing fails, return as-is
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
};

type CodeEditorProps = {
  /** Value to display/edit - for JSON, can be a string or object that will be stringified */
  value: string | unknown;
  /** Callback fired when the editor content changes (only in edit mode) */
  onChange?: (value: string) => void;
  /** Editor options */
  options?: editor.IStandaloneEditorConstructionOptions;
  /** Whether the editor is read-only (viewer mode) */
  readOnly?: boolean;
  /** Height of the editor - defaults to '100%' */
  height?: React.CSSProperties['height'];
  /** Minimum editor height - defaults to '200px' */
  minHeight?: React.CSSProperties['minHeight'];
  /** Additional class names */
  className?: string;
  /** Callback fired when the editor mounts, provides access to the editor instance */
  onMount?: (editor: editor.IStandaloneCodeEditor) => void;
  /** Callback fired when validation markers change (for error detection) */
  onValidate?: OnValidate;
  /** Language for syntax highlighting - defaults to 'json' */
  language?: string;
};

export const CodeEditor = ({
  value,
  onChange,
  readOnly = false,
  options,
  height = '100%',
  minHeight = '200px',
  className,
  onMount,
  onValidate,
  language = 'json'
}: CodeEditorProps) => {
  const { theme } = useTheme();
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);

  const formattedValue = useMemo(() => {
    // Only format as JSON if the language is JSON
    if (language === 'json') {
      return toJsonString(value);
    }
    // For other languages, return the value as-is (must be a string)
    return typeof value === 'string' ? value : String(value);
  }, [value, language]);

  const handleEditorDidMount: OnMount = editorInstance => {
    editorRef.current = editorInstance;
    onMount?.(editorInstance);
  };

  const handleChange: OnChange = newValue => {
    if (onChange && newValue !== undefined) {
      if (readOnly) {
        notify.error('Cannot edit a read-only editor');
        return;
      }
      onChange(newValue);
    }
  };

  return (
    <div
      className={cn('flex h-full w-full flex-col border bg-card', className)}
      style={{ height, minHeight }}
    >
      <Suspense fallback={<Skeleton className="h-full w-full" />}>
        <MonacoEditor
          defaultLanguage={language}
          height="100%"
          loading={<Skeleton className="h-full w-full" />}
          onChange={handleChange}
          onMount={handleEditorDidMount}
          onValidate={onValidate}
          options={{
            automaticLayout: true,
            copyWithSyntaxHighlighting: true,
            cursorBlinking: 'smooth',
            cursorSmoothCaretAnimation: 'on',
            minimap: { enabled: true },
            lineNumbers: 'on',
            codeLens: false,
            folding: true,
            foldingStrategy: 'auto',
            showFoldingControls: 'always',
            glyphMargin: false,
            renderWhitespace: 'selection',
            scrollBeyondLastLine: false,
            overviewRulerBorder: false,
            wordWrap: 'on',
            tabSize: 2,
            formatOnPaste: true,
            formatOnType: true,
            ...options,
            readOnly
          }}
          theme={theme === 'dark' ? 'vs-dark' : 'vs'}
          value={formattedValue}
        />
      </Suspense>
    </div>
  );
};
